import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import BookingConfirmed from "@/components/BookingConfirmed";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL = 2000;

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const token = params.get("token");
  const [state, setState] = useState("polling"); // polling | success | failed | timeout
  const [booking, setBooking] = useState(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    // Voucher-covered booking (no Stripe payment) — fetch directly by manage token
    if (token && !sessionId) {
      (async () => {
        try {
          const res = await api.get(`/bookings/manage/${token}`);
          setBooking(res.data);
          setState("success");
        } catch (e) {
          setState("failed");
        }
      })();
      return;
    }

    if (!sessionId) {
      setState("failed");
      return;
    }

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid" && res.data.booking) {
          setBooking(res.data.booking);
          setState("success");
          return;
        }
        if (res.data.status === "expired") {
          setState("failed");
          return;
        }
      } catch (e) {
        // keep polling on transient errors
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };
    poll();
  }, [sessionId, token]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-16 px-6" data-testid="booking-success-page">
      <div className="max-w-3xl mx-auto">
        {state === "polling" && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-12 text-center" data-testid="payment-processing">
            <Loader2 className="h-10 w-10 animate-spin text-[#4A5D23] mx-auto" />
            <h1 className="mt-6 font-serif text-2xl text-[#1C1917]">Confirming your payment…</h1>
            <p className="mt-2 text-[#57534E]">Please wait a moment, this won't take long.</p>
          </div>
        )}

        {state === "success" && booking && <BookingConfirmed booking={booking} />}

        {(state === "failed" || state === "timeout") && (
          <div className="bg-white rounded-2xl shadow-sm border border-[#E7E5E4] p-12 text-center" data-testid="payment-failed">
            <XCircle className="h-12 w-12 text-[#722F37] mx-auto" />
            <h1 className="mt-4 font-serif text-2xl text-[#1C1917]">
              {state === "timeout" ? "Still processing your payment" : "Payment not completed"}
            </h1>
            <p className="mt-2 text-[#57534E]">
              {state === "timeout"
                ? "Your payment is taking longer than expected. If you were charged, you'll receive a confirmation email shortly."
                : "We couldn't confirm your payment. No booking was made — please try again."}
            </p>
            <Link
              to="/#book"
              className="mt-6 inline-block px-6 py-3 rounded-full bg-[#4A5D23] text-white font-semibold hover:bg-[#3D4D1D] transition-all"
              data-testid="retry-booking-link"
            >
              Back to booking
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
